// shell/components/table/column-renderer-factory.ts

import { ColumnDef, ColumnType, CustomColumnType, RenderedCell } from './types';
import { ColumnRenderer, CellRenderContext, CellUpdateType } from './columns/column-base';
import { TextColumnRenderer } from './columns/text-column';
import { TitleColumnRenderer } from './columns/title-column';
import { ImageColumnRenderer } from './columns/image-column';
import { CanvasColumnRenderer } from './columns/canvas-column';
import { IconToggleColumnRenderer } from './columns/icon-toggle-column';
import { LabeledTextColumnRenderer } from './columns/labeled-text-column';
import { BadgeColumnRenderer } from './columns/badge-column';
import { IconTextColumnRenderer } from './columns/icon-text-column';
import { HeatMapShader } from '../heat-map';
import { IDisposable } from '../types';

/**
 * Factory for creating column renderers from column type definitions.
 *
 * Responsibilities:
 * - Creates and caches renderer instances by column key
 * - Owns the shared HeatMapShader instance (injected into all renderers)
 * - Provides safe cell rendering with error boundaries
 */
export class ColumnRendererFactory<T> implements IDisposable {
    private readonly renderers = new Map<string, ColumnRenderer<T>>();

    /** Shared HeatMapShader instance for all renderers */
    private readonly heatMapShader = new HeatMapShader();

    /**
     * Get or create a renderer for the given column.
     * Renderers are cached by column key.
     */
    getRenderer(column: ColumnDef<T>): ColumnRenderer<T> {
        let renderer = this.renderers.get(column.key);
        if (renderer) {
            return renderer;
        }

        renderer = this.createRenderer(column.type);
        renderer.setHeatMapShader(this.heatMapShader);
        this.renderers.set(column.key, renderer);
        return renderer;
    }

    /**
     * Render a grid cell with error boundary.
     * Returns RenderedCell for tracking.
     */
    renderGridCell(column: ColumnDef<T>, ctx: CellRenderContext<T>, cellWrapper: HTMLElement): RenderedCell<T> {
        const renderer = this.getRenderer(column);

        cellWrapper.classList.add(renderer.getGridCellClass());

        let contentElement: HTMLElement | null = null;
        try {
            contentElement = renderer.buildGridCell(ctx, cellWrapper);
            if (contentElement) {
                cellWrapper.appendChild(contentElement);
            }
        } catch (e) {
            console.error(`Column ${column.key} grid render failed:`, e);
            contentElement = renderer.buildErrorPlaceholder();
            cellWrapper.appendChild(contentElement);
        }

        return {
            cellElement: cellWrapper,
            contentElement,
            column,
            rowIndex: ctx.rowIndex
        };
    }

    /**
     * Render a table cell with error boundary.
     * Returns RenderedCell for tracking.
     *
     * Note: Cell can be HTMLElement (CSS Grid div) or HTMLTableCellElement.
     * Both work since we only use basic DOM methods (classList, appendChild).
     */
    renderTableCell(column: ColumnDef<T>, ctx: CellRenderContext<T>, cell: HTMLElement): RenderedCell<T> {
        const renderer = this.getRenderer(column);

        cell.classList.add(renderer.getTableCellClass());

        let contentElement: HTMLElement | null = null;
        try {
            contentElement = renderer.buildTableCell(ctx, cell);
            if (contentElement) {
                cell.appendChild(contentElement);
            }
        } catch (e) {
            console.error(`Column ${column.key} table render failed:`, e);
            contentElement = renderer.buildErrorPlaceholder();
            cell.appendChild(contentElement);
        }

        return {
            cellElement: cell,
            contentElement,
            column,
            rowIndex: ctx.rowIndex
        };
    }

    /**
     * Update a grid cell with error boundary.
     * Returns updated RenderedCell (content element may have changed).
     */
    updateGridCell(renderedCell: RenderedCell<T>, ctx: CellRenderContext<T>, type: CellUpdateType): RenderedCell<T> {
        const renderer = this.getRenderer(renderedCell.column);

        try {
            const result = renderer.updateGridCell(
                ctx,
                renderedCell.cellElement,
                renderedCell.contentElement,
                type
            );
            return {
                ...renderedCell,
                contentElement: result.contentElement,
                rowIndex: ctx.rowIndex
            };
        } catch (e) {
            console.error(`Column ${renderedCell.column.key} grid update failed:`, e);
            // On error, rebuild the cell
            renderedCell.cellElement.innerHTML = '';
            return this.renderGridCell(renderedCell.column, ctx, renderedCell.cellElement);
        }
    }

    /**
     * Update a table cell with error boundary.
     * Returns updated RenderedCell (content element may have changed).
     */
    updateTableCell(renderedCell: RenderedCell<T>, ctx: CellRenderContext<T>, type: CellUpdateType): RenderedCell<T> {
        const renderer = this.getRenderer(renderedCell.column);

        try {
            const result = renderer.updateTableCell(
                ctx,
                renderedCell.cellElement,
                renderedCell.contentElement,
                type
            );
            return {
                ...renderedCell,
                contentElement: result.contentElement,
                rowIndex: ctx.rowIndex
            };
        } catch (e) {
            console.error(`Column ${renderedCell.column.key} table update failed:`, e);
            // On error, rebuild the cell
            renderedCell.cellElement.innerHTML = '';
            return this.renderTableCell(
                renderedCell.column,
                ctx,
                renderedCell.cellElement
            );
        }
    }

    /**
     * Clean up a rendered cell before removal or recycling.
     */
    cleanupCell(renderedCell: RenderedCell<T>): void {
        const renderer = this.renderers.get(renderedCell.column.key);
        if (renderer) {
            renderer.cleanupElement(renderedCell.cellElement);
            if (renderedCell.contentElement) {
                renderer.cleanupElement(renderedCell.contentElement);
            }
        }
    }

    /**
     * Create a new renderer instance for the given column type.
     */
    private createRenderer(type: ColumnType<T>): ColumnRenderer<T> {
        switch (type.kind) {
            case 'text':
                return new TextColumnRenderer(type);
            case 'title':
                return new TitleColumnRenderer(type);
            case 'image':
                return new ImageColumnRenderer(type);
            case 'canvas':
                return new CanvasColumnRenderer(type);
            case 'icon-toggle':
                return new IconToggleColumnRenderer(type);
            case 'labeled-text':
                return new LabeledTextColumnRenderer(type);
            case 'badge':
                return new BadgeColumnRenderer(type);
            case 'icon-text':
                return new IconTextColumnRenderer(type);
            case 'custom':
                return this.createCustomRenderer(type);
            default:
                // TypeScript exhaustiveness check for built-in types
                const _exhaustive: never = type;
                throw new Error(`Unknown column type: ${(_exhaustive as ColumnType<T>).kind}`);
        }
    }

    /**
     * Create renderer for custom column type.
     */
    private createCustomRenderer(type: CustomColumnType<T>): ColumnRenderer<T> {
        if (typeof type.renderer === 'function') {
            return type.renderer(type);
        }
        return type.renderer;
    }

    /**
     * Clear cached renderer for a column (e.g., when column config changes).
     */
    clearRenderer(columnKey: string): void {
        const renderer = this.renderers.get(columnKey);
        if (renderer) {
            renderer.dispose();
            this.renderers.delete(columnKey);
        }
    }

    /**
     * Dispose all cached renderers and shared resources.
     */
    dispose(): void {
        for (const renderer of this.renderers.values()) {
            renderer.dispose();
        }
        this.renderers.clear();
    }
}
